/**
 * Weekly Digest — Monday morning "Your Week Ahead" popup.
 * Summarizes overdue tasks + neglected leads and lets the rep add missed
 * items straight to their to-do list, or email themselves the digest.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Sun, AlertTriangle, Clock, Mail, ListPlus, X } from 'lucide-react';

export function WeeklyDigestModal({ open, onClose }) {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todoDate, setTodoDate] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.getWeeklyDigest().then(setDigest).catch(() => toast.error('Failed to load digest')).finally(() => setLoading(false));
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    setTodoDate(tomorrow.toISOString().slice(0, 10));
  }, [open]);

  const dismiss = async () => {
    try { await api.dismissDigest(); } catch (e) { /* non-blocking */ }
    onClose();
  };

  const addMissedToTodo = async () => {
    setBusy(true);
    try {
      const res = await api.addMissedToTodo({ due_date: todoDate, due_time: '09:00' });
      toast.success(`Added ${res.tasks_created} follow-up task(s) to your to-do list`);
    } catch (e) {
      toast.error('Failed to add missed items');
    } finally {
      setBusy(false);
    }
  };

  const emailMe = async () => {
    setBusy(true);
    try {
      const res = await api.sendDigestNow();
      toast.success(res.delivery_label);
    } catch (e) {
      toast.error('Failed to send digest email');
    } finally {
      setBusy(false);
    }
  };

  const money = digest?.money_action_summary || { scheduled_money_hours: 0, scheduled_total_hours: 0 };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent data-testid="weekly-digest-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sun className="h-5 w-5 text-amber-500" /> Your Week Ahead</DialogTitle>
          <DialogDescription>Here's what needs attention before you dive into the week.</DialogDescription>
        </DialogHeader>

        {loading || !digest ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="weekly-digest-loading">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Clock className="h-4 w-4 text-red-500" /> {digest.overdue_count} overdue task{digest.overdue_count === 1 ? '' : 's'}
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> {digest.neglected_count} opportunit{digest.neglected_count === 1 ? 'y needs' : 'ies need'} attention
            </div>
            <p className="text-xs text-muted-foreground">
              Money-Action Hours: {money.scheduled_money_hours}h of {money.scheduled_total_hours}h scheduled this week
            </p>

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <Input type="date" value={todoDate} onChange={(e) => setTodoDate(e.target.value)} className="max-w-[160px]" data-testid="digest-todo-date-input" />
              <Button size="sm" variant="outline" onClick={addMissedToTodo} disabled={busy} data-testid="digest-add-missed-btn">
                <ListPlus className="h-3.5 w-3.5 mr-1.5" /> Add missed items to my to-do list
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="justify-between sm:justify-between">
          <Button variant="ghost" onClick={emailMe} disabled={busy || loading} data-testid="digest-email-me-btn">
            <Mail className="h-4 w-4 mr-2" /> Email me this digest
          </Button>
          <Button onClick={dismiss} data-testid="digest-dismiss-btn"><X className="h-4 w-4 mr-2" /> Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
